import React from 'react';
import { FEATURE_LABELS, useFeatureAccess, type FeatureKey, type FeatureMode } from '../utils/featureAccess';

/**
 * Preview markers for a gated page this account can open while the page is
 * not yet open to everyone (utils/featureAccess.ts `preview`):
 *   "Super admin preview"  mode superadmins — only super admins see it
 *   "Preview"              mode selected / admins — some or all admins see it
 *   (nothing)              mode everyone
 * Who sees each page is set in that page's Feature access tab in the admin
 * panel (super admins only): For You & What's New → Feature access for the
 * new For You and What's New, Ocean → Feature access for OCEAN.
 */

export const FEATURE_ACCESS_ADMIN_URL: Record<FeatureKey, string> = {
  foryou: '/admin?group=foryou_whatsnew&tab=feature_access',
  whatsnew: '/admin?group=foryou_whatsnew&tab=feature_access',
  ocean: '/admin?group=ocean&tab=feature_access',
};

const isSuper = (mode?: FeatureMode) => mode === 'superadmins';

const label = (mode?: FeatureMode, short?: boolean) => {
  if (isSuper(mode)) return short ? 'Super' : 'Super admin preview';
  return 'Preview';
};

const tip = (mode?: FeatureMode) => {
  if (isSuper(mode)) return 'Super admin preview: only super admins see this page.';
  if (mode === 'selected') return 'Preview: only selected admins see this page; members do not see it yet.';
  return 'Preview: admins see this page; members do not see it yet.';
};

/** A small pill. `corner` pins it to the top-right of a relative parent (SideNav icons). */
export const AdminPreviewBadge: React.FC<{ corner?: boolean; short?: boolean; mode?: FeatureMode }> =
  ({ corner, short, mode }) => {
  if (mode === 'everyone') return null;
  return (
    <span title={tip(mode)} aria-label={label(mode)}
      className={`inline-flex items-center rounded-full font-grotesk font-semibold leading-none whitespace-nowrap border
        ${isSuper(mode)
          ? 'bg-violet-100 text-violet-800 border-violet-300'
          : 'bg-amber-100 text-amber-800 border-amber-300'}
        ${corner
          ? 'absolute -top-1 -right-1 z-10 px-1 py-[2px] text-[7px] uppercase tracking-wide'
          : 'px-2 py-[3px] text-[10px]'}`}>
      {label(mode, short)}
    </span>
  );
};

/** One slim row above a previewed page (the page header's badge) — from lg
 *  up; below lg the mobile header carries the badge (App.tsx). */
export const AdminPreviewBar: React.FC<{ feature: FeatureKey }> = ({ feature }) => {
  const { features } = useFeatureAccess();
  const mode = features[feature].mode;
  if (mode === 'everyone') return null;
  const members = feature === 'foryou' ? 'Members still see the previous For You page.'
    : feature === 'ocean' ? 'Members still see the Coming Soon page.'
      : 'Members do not see this page yet.';
  const who = isSuper(mode) ? 'Only super admins see it.'
    : mode === 'selected' ? 'Selected admins see it.' : 'Admins see it.';
  return (
    <div className={`hidden lg:flex items-center gap-2 flex-wrap px-3 py-1.5 mb-3 rounded-xl border text-[12px]
      text-grayscale-1100 font-grotesk flex-shrink-0 ${isSuper(mode)
        ? 'border-violet-200 bg-violet-50' : 'border-amber-200 bg-amber-50'}`}>
      <AdminPreviewBadge mode={mode} />
      <span className="min-w-0">{FEATURE_LABELS[feature]} · {who} {members}</span>
      {/* Only a super admin can change who sees it; mode "superadmins" proves
          this account is one. */}
      {isSuper(mode) && (
        <a href={FEATURE_ACCESS_ADMIN_URL[feature]} className="ml-auto text-primary-700 underline whitespace-nowrap">
          Feature access
        </a>
      )}
    </div>
  );
};
